"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePreview } from "../preview/use-preview";
import { captureVisitorPage } from "../visitor-page/capture-visitor-page";
import { sessionPageParams } from "../visitor-page/session-page-params";
import type { ContentSlots } from "../widget-config";
import { useWidgetInstance } from "../widget-instance-context";
import { fetchSessionContent } from "./session-content";

/** Max wait for a visitor token before the welcome stops waiting and uses the public projection. */
export const TOKEN_WAIT_TIMEOUT_MS = 6_000;

export interface SessionContent {
  /** The session projection that replaces the public slots; `null` = keep the public ones. */
  slots: ContentSlots | null;
  /** Session kind: the BE's answer, or ANONYMOUS once an anonymous handshake is confirmed.
   *  `null` before that, and for a verified visitor until the session answers. */
  visitors: "ANONYMOUS" | "SIGNED_IN" | null;
  /** The contact is blocked, per the session answer; `null` = unknown (no answer yet, or an old BE). */
  blocked?: boolean | null;
  /** BE feature flags of the session answer; `null` = none known (use the public config's). */
  capabilities?: string[] | null;
  /** The wait is over (answered, failed, timed out, or there is nothing to wait for). */
  settled: boolean;
  /** Refetch for the same token (after a pre-chat submit or a profile save). */
  refresh: () => void;
}

interface State {
  token: string | null;
  slots: ContentSlots | null;
  visitors: "ANONYMOUS" | "SIGNED_IN" | null;
  blocked: boolean | null;
  capabilities: string[] | null;
  settled: boolean;
  /** The visitor token the BE rejected, until a different one arrives. */
  rejected?: string | null;
}

/**
 * Holds the session content of the current visitor token. The token changes on every handshake,
 * restore, re-auth and logout, which covers those triggers; `refresh()` covers the in-session
 * ones. Latest-wins (a superseded request is aborted AND ignored). Memory only: nothing here is
 * persisted, so a `session="isolated"` embed leaves no trace either.
 */
export function useSessionContent(opts: {
  publicKey: string | null;
  token: string | null;
  locale: string;
  enabled: boolean;
  /** A confirmed ANONYMOUS handshake (token present, not verified). Counts as "anonymous" for
   *  matching before the session answers; SIGNED_IN is only ever taken from the answer. */
  anonymous?: boolean;
  /** Who the token belongs to (people id + verified). A silent renew of the same visitor changes only
   *  the token: it must neither refetch nor drop the content. Defaults to the token itself. */
  visitorKey?: string | null;
  /** The embed kind, to read the HOST page (the iframe's own location is not it). The page is sent so the BE
   *  can render `page.*` variables; read at fetch time, so a navigation alone never refetches. */
  pageKind?: "iframe" | "web-component";
}): SessionContent {
  const { publicKey, token, locale, anonymous = false } = opts;
  const identity = token ? (opts.visitorKey ?? token) : null;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const instance = useWidgetInstance();
  // A rejected preview token pauses the fetch (it would only fail again); the next visitor token,
  // minted by the re-handshake with the new preview token, runs it. The preview token itself is read
  // at fetch time, not tracked: a swap alone must not refetch with the OLD visitor JWT.
  const previewPaused = usePreview().paused;
  const enabled = opts.enabled && !previewPaused;
  const [state, setState] = useState<State>({
    token: null,
    slots: null,
    visitors: null,
    blocked: null,
    capabilities: null,
    settled: false,
  });
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  const controller = useRef<AbortController | null>(null);

  // A different token is a different person (or a re-auth): never show the previous content.
  const tokenChanged = state.token !== identity;
  if (tokenChanged) {
    setState({
      token: identity,
      slots: null,
      visitors: null,
      blocked: null,
      capabilities: null,
      settled: false,
    });
  } else if (state.rejected && state.rejected !== token) {
    // The BE rejected the previous token and a recovery handshake of the SAME visitor minted this
    // one: refetch even though the identity is unchanged.
    setState({ ...state, rejected: null });
    setNonce((n) => n + 1);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: `nonce` is the explicit refetch trigger
  useEffect(() => {
    const token = tokenRef.current;
    if (!enabled || !publicKey || !token || !identity) return;
    controller.current?.abort();
    const ctl = new AbortController();
    controller.current = ctl;
    const mine = ++seq.current;
    const previewTokenUsed = instance.preview.token;
    fetchSessionContent({
      publicKey,
      token,
      locale,
      ...(opts.pageKind ? { page: sessionPageParams(captureVisitorPage(opts.pageKind)) } : {}),
      previewToken: previewTokenUsed,
      signal: ctl.signal,
    }).then((res) => {
      if (mine !== seq.current) return; // a newer request owns the state
      if (res.kind === "unauthorized" && res.previewReason) {
        instance.preview.report(res.previewReason, previewTokenUsed);
      }
      setState((prev) => {
        if (prev.token !== identity) return prev;
        if (res.kind === "ok") {
          return {
            token: identity,
            slots: res.slots,
            visitors: res.visitors,
            blocked: res.blocked,
            capabilities: res.capabilities,
            settled: true,
          };
        }
        if (res.kind === "unauthorized") {
          return {
            token: identity,
            slots: null,
            visitors: null,
            blocked: null,
            capabilities: null,
            settled: true,
            rejected: token,
          };
        }
        return { ...prev, settled: true };
      });
    });
    return () => {
      ctl.abort();
    };
  }, [enabled, publicKey, identity, locale, nonce, instance]);

  // No token (yet): give the handshake a bounded time, then use the public projection.
  useEffect(() => {
    if (!enabled || token) return;
    const t = setTimeout(() => setState((p) => ({ ...p, settled: true })), TOKEN_WAIT_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [enabled, token]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const live = enabled && !tokenChanged;
  const slots = live ? state.slots : null;
  const visitors = live ? (state.visitors ?? (anonymous && token ? "ANONYMOUS" : null)) : null;
  const blocked = live ? state.blocked : null;
  const capabilities = live ? state.capabilities : null;
  const settled = !enabled || (!tokenChanged && state.settled);
  // Memoised: consumers key effects and context on this object.
  return useMemo(
    () => ({ slots, visitors, blocked, capabilities, settled, refresh }),
    [slots, visitors, blocked, capabilities, settled, refresh],
  );
}
